import { useUser } from "@clerk/react";
import { useEffect, useRef } from "react";

import { useWatchlistPermissions } from "~/feature/watchlist/hooks/useWatchlistPermissions";
import { readWatchlistIdsFromStorage } from "~/feature/watchlist/utils/storage";
import { useSyncUserWatchlistMutation } from "~/feature/watchlist/watchlistApi";
import type { IWatchlistMetadata } from "~/types/watchlist";
import { handleError } from "~/utils/handleError";
import { isNewAccountSignIn } from "~/utils/isNewAccountSignIn";

interface IUseWatchlistSyncProps {
  isReady: boolean;
  metadata: IWatchlistMetadata | null;
  onSynced: (ids: string[], metadata: IWatchlistMetadata) => void;
  onCleanup: () => void;
}

const isUserAtLimit = (metadata: IWatchlistMetadata): boolean => {
  if (metadata.maxLimit === null) {
    return false;
  }
  return metadata.count >= metadata.maxLimit;
};

export const useWatchlistSync = ({
  isReady,
  metadata,
  onSynced,
  onCleanup,
}: IUseWatchlistSyncProps) => {
  const { hasLimitThree, hasNoLimits, hasAnyWatchlistFeature } =
    useWatchlistPermissions();
  const { user } = useUser();
  const [syncWatchlist] = useSyncUserWatchlistMutation();
  const didRunRef = useRef(false);

  useEffect(() => {
    if (!isReady || !metadata || didRunRef.current) {
      return;
    }

    if (!hasAnyWatchlistFeature) {
      return;
    }

    didRunRef.current = true;

    const localIds = readWatchlistIdsFromStorage();
    const hasLocalIds = localIds.length > 0;
    const userAtLimit =
      hasLimitThree && !hasNoLimits && isUserAtLimit(metadata);
    const shouldSync = hasLocalIds && !userAtLimit && isNewAccountSignIn(user);

    const runSync = async () => {
      try {
        if (shouldSync) {
          const response = await syncWatchlist({ ids: localIds }).unwrap();
          onSynced(response.ids, response.metadata);
        }
      } catch (error) {
        handleError(error);
      } finally {
        onCleanup();
      }
    };

    void runSync();
  }, [
    hasAnyWatchlistFeature,
    hasLimitThree,
    hasNoLimits,
    isReady,
    metadata,
    onCleanup,
    onSynced,
    syncWatchlist,
    user,
  ]);
};
